import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

const PRIORITY_COLORS = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b82f6', low: '#6b7280' };

export default function ReportIssuePage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', priority: '', field_values: {} });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAvailableIssueTemplates()
      .then(setTemplates)
      .catch(err => console.error('Failed to load templates:', err))
      .finally(() => setLoading(false));
  }, []);

  function selectTemplate(template) {
    setSelectedTemplate(template);
    setForm({
      title: '',
      description: '',
      priority: template.priority_default || 'normal',
      field_values: {},
    });
    setError('');
  }

  function setField(key, val) {
    setForm(f => ({ ...f, field_values: { ...f.field_values, [key]: val } }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const item = await api.submitIssueReport({
        template_id: selectedTemplate.id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        field_values: form.field_values,
      });
      navigate(`/workflow/items/${item.id}`);
    } catch (err) {
      setError(err.message || 'Failed to submit issue');
    }
    setSubmitting(false);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;

  // Template selection screen
  if (!selectedTemplate) {
    // Group by category
    const byCategory = {};
    for (const t of templates) {
      const cat = t.category || 'General';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(t);
    }

    return (
      <div style={{ padding: '24px 32px', maxWidth: 700 }}>
        <div className="page-header">
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Report an Issue</h1>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
              Select the type of issue you want to report
            </p>
          </div>
        </div>

        {templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>No issue templates available</div>
            <div style={{ fontSize: 13 }}>Contact your administrator to set up issue templates</div>
          </div>
        ) : (
          Object.entries(byCategory).map(([cat, catTemplates]) => (
            <div key={cat} style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                {cat}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {catTemplates.map(t => (
                  <div key={t.id} onClick={() => selectTemplate(t)} style={{
                    padding: '16px 20px', backgroundColor: '#fff', borderRadius: 10,
                    border: '1px solid #e5e7eb', cursor: 'pointer',
                  }} className="hover-lift">
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: 13, color: '#6b7280' }}>{t.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  // Issue form
  return (
    <div style={{ padding: '24px 32px', maxWidth: 600 }}>
      <button onClick={() => setSelectedTemplate(null)} style={{
        background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer',
        fontSize: 13, fontWeight: 600, marginBottom: 12, padding: 0,
      }}>
        ← Back to templates
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
        {selectedTemplate.name}
      </h1>
      {selectedTemplate.description && (
        <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 20px' }}>{selectedTemplate.description}</p>
      )}

      {error && (
        <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label className="sales-label">Title *</label>
            <input className="sales-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Brief summary of the issue" style={{ width: '100%' }} required />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="sales-label">Description</label>
            <textarea className="sales-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Provide details, context, or instructions..." rows={3} style={{ width: '100%', resize: 'vertical' }} />
          </div>
          <div>
            <label className="sales-label">Priority</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['low', 'normal', 'high', 'urgent'].map(p => (
                <button key={p} type="button" onClick={() => setForm(f => ({ ...f, priority: p }))} style={{
                  flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: form.priority === p ? `2px solid ${PRIORITY_COLORS[p]}` : '2px solid #e5e7eb',
                  backgroundColor: form.priority === p ? `${PRIORITY_COLORS[p]}10` : '#fff',
                  color: form.priority === p ? PRIORITY_COLORS[p] : '#6b7280',
                  textTransform: 'capitalize',
                }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Custom fields from template */}
        {selectedTemplate.fields && selectedTemplate.fields.length > 0 && (
          <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Details</h3>
            {selectedTemplate.fields.map((field) => (
              <div key={field.key} style={{ marginBottom: 12 }}>
                <label className="sales-label">{field.label}{field.required ? ' *' : ''}</label>
                {field.type === 'textarea' ? (
                  <textarea className="sales-input" value={form.field_values[field.key] || ''}
                    onChange={e => setField(field.key, e.target.value)} rows={3}
                    style={{ width: '100%', resize: 'vertical' }} required={field.required} />
                ) : field.type === 'select' && field.options ? (
                  <select className="sales-input" value={form.field_values[field.key] || ''}
                    onChange={e => setField(field.key, e.target.value)} style={{ width: '100%' }} required={field.required}>
                    <option value="">Select...</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="sales-input" type={field.type === 'number' ? 'number' : 'text'}
                    value={form.field_values[field.key] || ''}
                    onChange={e => setField(field.key, e.target.value)} style={{ width: '100%' }} required={field.required} />
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={() => setSelectedTemplate(null)} style={{ color: '#6b7280' }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting || !form.title.trim()}
            style={{ backgroundColor: '#ef4444', color: '#fff', fontWeight: 600 }}>
            {submitting ? 'Submitting...' : 'Submit Issue'}
          </button>
        </div>
      </form>
    </div>
  );
}
